import QuestionarioOpcaoResposta from './QuestionarioOpcaoResposta';

const TIPO_TEXTO = 'texto';
const TIPO_INTEIRO = 'inteiro';
const TIPO_NUMERICO = 'numerico';
const TIPO_ESCOLHA = 'escolha';
const TIPO_MULTIPLAESCOLHA = 'multiplaEscolha';
const TIPO_FOTO = 'foto';
const TIPO_TEXTO_FIXO = 'textoFixo';

/**
 * Tipos de questões possíveis
 */
const QuestaoTipo = [ TIPO_TEXTO, TIPO_INTEIRO, TIPO_NUMERICO, TIPO_ESCOLHA, TIPO_MULTIPLAESCOLHA, TIPO_FOTO ];

/**
 * Objeto que representa uma questão do questionário
 */
class QuestionarioQuestao {

    /**
     * Cria uma nova instância de uma questão
     * Caso os parâmetros não sejam informados, cria com as propriedades vazias
     * 
     * @param {Number|null} id Id da questão na API
     * @param {String|null} titulo Título da questão
     * @param {Boolean|null} obrigatoria Indica se o preenchimento da questão é obrigatório
     * @param {Boolean|null} exibeGrafico Indica se a questão gera um gráfico no relatório
     * @param {String|null} tipo Tipo da questão
     * @param {Number|null} ordem Ordem da questão na seção
     * @param {Boolean|null} opcaoOutros Indica se a questão irá ter uma opção "Outros" no preenchimento
     * @param {Boolean|null} limitaValor Indica se a questão irá limitar o valor da resposta
     * @param {Number|null} valorMinimo Valor mínimo possível para a resposta
     * @param {Number|null} valorMaximo Valor máximo possível para a resposta
     * @param {String|null} conteudoTexto Conteúdo fixo da questão
     * @param {Boolean|null} exibeResultado Indica se a questão será exibida na avaliação ou no resultado
     * @param {Array<QuestionarioOpcaoResposta>|null} opcoes Opções de resposta para a questão
     */
    constructor (id, titulo, obrigatoria, exibeGrafico, tipo, ordem, opcaoOutros, limitaValor, valorMinimo, valorMaximo, conteudoTexto, exibeResultado, opcoes) {
        /** @member {number} id Id da questão na API */
        this.id = id ?? null;
        /** @member {string} titulo Título da questão */
        this.titulo = titulo ?? '';
        /** @member {boolean} obrigatoria Indica se o preenchimento da questão é obrigatório */
        this.obrigatoria = obrigatoria ?? false;
        /** @param {Boolean|null} exibeGrafico Indica se a questão gera um gráfico no relatório */
        this.exibeGrafico = exibeGrafico ?? false;
        /** @member {String|null} tipo Tipo da questão */
        this.tipo = tipo ?? '';
        /** @member {boolean} ordem Ordem da seção no questionário */
        this.ordem = ordem ?? 1;
        /** @member {Boolean|null} opcaoOutros Indica se a questão irá ter uma opção "Outros" no preenchimento */
        this.opcaoOutros = opcaoOutros ?? false;
        /** @member {Boolean|null} limitaValor Indica se a questão irá limitar o valor da resposta */
        this.limitaValor = limitaValor ?? false;
        /** @member {Number|null} valorMinimo Valor mínimo possível para a resposta */
        this.valorMinimo = valorMinimo ?? 0;
        /** @member {Number|null} valorMaximo Valor máximo possível para a resposta */
        this.valorMaximo = valorMaximo ?? 0;
        /** @member {String|null} conteudoTexto Conteúdo fixo da questão */
        this.conteudoTexto = conteudoTexto ?? '';
        /** @member {Boolean|null} exibeResultado Indica se a questão será exibida na avaliação ou no resultado */
        this.exibeResultado = exibeResultado ?? false;
        /** @member {Array<QuestionarioOpcaoResposta>|null} opcoes Opções de resposta para a questão */
        this.opcoes = opcoes ?? [];
    }

    /**
     * Cria uma cópia do objeto
     * @param {QuestionarioQuestao} questao Questão a ser clonada
     * @returns {QuestionarioQuestao} Novo objeto com os mesmos valores que o original
     */
    static clone(questao) {
        let opcoes = null;
        if (questao.opcoes)
            opcoes = questao.opcoes.map(opcao => QuestionarioOpcaoResposta.clone(opcao));
        return new QuestionarioQuestao(
            questao.id, 
            questao.titulo, 
            questao.obrigatoria, 
            questao.exibeGrafico, 
            questao.tipo, 
            questao.ordem, 
            questao.opcaoOutros, 
            questao.limitaValor, 
            questao.valorMinimo, 
            questao.valorMaximo,
            questao.conteudoTexto,
            questao.exibeResultado,
            opcoes);
    }

    /**
     * Compara duas questões do questionário, retornando verdadeiro se elas forem iguais
     * @param {QuestionarioQuestao} obj1 Primeiro objeto a ser comparado
     * @param {QuestionarioQuestao} obj2 Segundo objeto a ser comparado
     * @returns {Boolean} Retorna verdadeiro se os dois objetos forem iguais
     */
    static compare(obj1, obj2) {
        //Se os dois possuírem o mesmo endereço de memória
        if (obj1 === obj2)
            return true;
        //Se um dos dois for nulo
        if (!obj1 || !obj2)
            return false;
        //Compara as propriedades
        let result = obj1.id === obj2.id
                  && obj1.titulo === obj2.titulo
                  && obj1.obrigatoria === obj2.obrigatoria
                  && obj1.tipo === obj2.tipo
                  && obj1.ordem === obj2.ordem
                  && obj1.exibeGrafico === obj2.exibeGrafico
                  && obj1.opcaoOutros === obj2.opcaoOutros
                  && obj1.limitaValor === obj2.limitaValor 
                  && obj1.valorMinimo === obj2.valorMinimo 
                  && obj1.valorMaximo === obj2.valorMaximo
                  && obj1.conteudoTexto === obj2.conteudoTexto
                  && obj1.exibeResultado === obj2.exibeResultado;

        //Caso as propriedades sejam iguais, verifica as listas
        if (result) {
            return QuestionarioQuestao.compareOptions(obj1.opcoes, obj2.opcoes);
        } else {
            return false;
        }
    }

    /**
     * Compara a lista de opções de cada questão
     * @param {Array<QuestionarioOpcaoResposta>} opt1 Lista de opções da primeira questão
     * @param {Array<QuestionarioOpcaoResposta>} opt2 Lista de opções da segunda questão
     * @returns {Boolean} Retorna verdadeiro se os itens das duas listas forem iguais
     */
    static compareOptions(opt1, opt2) {
        //Se as duas listas possuírem o mesmo endereço de memória
        if (opt1 === opt2)
            return true;
        //Se uma das duas for nula
        if (!opt1 || !opt2)
            return false;
        //Se os tamanhos forem diferentes
        if (opt1.length !== opt2.length)
            return false;
        //Verifica se existe algum item que seja diferente entre as listas
        for (let i = 0; i < opt1.length; i++) {
            if (!QuestionarioOpcaoResposta.compare(opt1[i], opt2[i]))
                return false;
        }
        return true;
    }
}

export default QuestionarioQuestao;
export { QuestaoTipo, TIPO_TEXTO, TIPO_INTEIRO, TIPO_NUMERICO, TIPO_ESCOLHA, TIPO_MULTIPLAESCOLHA, TIPO_FOTO, TIPO_TEXTO_FIXO };